/**
 * Compose + ViewModel integration helpers for Koin in Kobweb (Compose HTML).
 *
 * Why not koin-compose?
 * `koin-compose` and `koin-compose-viewmodel` target Compose UI / Compose Multiplatform.
 * Kobweb uses Compose HTML which shares only the runtime, not the lifecycle or
 * ViewModelStoreOwner APIs. These lightweight helpers replicate the same DX:
 *
 * | Android / CMP             | This file                     |
 * |---------------------------|-------------------------------|
 * | `koinInject()`            | `koinInject()`                |
 * | `koinViewModel()`         | `koinViewModel()`             |
 * | (raw, outside composable) | `koinGet()`                   |
 *
 * Usage:
 * ```kotlin
 * // Inject any dependency in a composable
 * val repo: MyRepository = koinInject()
 *
 * // Inject + lifecycle-manage a ViewModel in a composable
 * val vm: MyViewModel = koinViewModel()
 *
 * // Resolve inside a non-composable lambda (e.g., rememberViewModel factory)
 * val vm = rememberViewModel { koinGet<MyViewModel>() }
 * ```
 */
package org.moxultimate.topwithmox.di

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import org.koin.core.context.GlobalContext
import org.koin.core.parameter.ParametersDefinition
import org.koin.core.qualifier.Qualifier
import org.moxultimate.topwithmox.core.base.ViewModel
import org.moxultimate.topwithmox.core.base.rememberViewModel

/**
 * Resolves a Koin-managed dependency WITHOUT [remember].
 *
 * Use this inside non-composable lambdas where `remember` is not available —
 * most commonly inside the factory lambda of [rememberViewModel].
 * Prefer [koinInject] when calling directly from a composable scope.
 *
 * @param qualifier optional Koin qualifier for named bindings.
 * @param parameters optional dynamic parameters passed to the factory.
 */
inline fun <reified T : Any> koinGet(
    qualifier: Qualifier? = null,
    noinline parameters: ParametersDefinition? = null,
): T = GlobalContext.get().get(qualifier, parameters)

/**
 * Retrieves and [remember]s a Koin-managed dependency inside a Composable.
 *
 * The dependency is resolved once from [GlobalContext] and memoised for the
 * lifetime of the composable. For `factory` bindings, a new instance is
 * created per composable invocation (not per recomposition).
 *
 * @param qualifier optional Koin qualifier for named bindings.
 * @param parameters optional dynamic parameters passed to the factory.
 */
@Composable
inline fun <reified T : Any> koinInject(
    qualifier: Qualifier? = null,
    noinline parameters: ParametersDefinition? = null,
): T = remember(qualifier) {
    GlobalContext.get().get(qualifier, parameters)
}

/**
 * Retrieves a Koin-managed [ViewModel] and binds its lifecycle to the
 * current composable — equivalent to `koinViewModel()` in koin-compose.
 *
 * Internally combines [koinGet] + [rememberViewModel] so the ViewModel is:
 * - Created once via Koin (respects `factory` / `single` bindings).
 * - Cleared (scope cancelled) automatically when the composable leaves.
 *
 * @param qualifier optional Koin qualifier for named bindings.
 * @param parameters optional dynamic parameters passed to the Koin factory.
 */
@Composable
inline fun <reified VM : ViewModel> koinViewModel(
    qualifier: Qualifier? = null,
    noinline parameters: ParametersDefinition? = null,
): VM = rememberViewModel { koinGet(qualifier, parameters) }
